/*
 * Virtual try-on popup.
 * 1. Not signed in        -> ask to sign in (Shopify customer accounts).
 * 2. No WhatsApp number   -> ask for it once, saved to the customer in Shopify via our server.
 * 3. Tries left today     -> pick a photo and run the try-on on our Cloudflare Worker.
 * Identity, the number check and the daily limit all live on the server
 * (App Proxy at /apps/tryon), so the page only shows what the server says.
 */
(() => {
  const dataEl = document.getElementById('SuitShopTryOnData');
  const modal = document.getElementById('SuitShopTryOn');
  const buttonTemplate = document.getElementById('SuitShopTryOnButton');
  if (!dataEl || !modal || !buttonTemplate) return;

  const data = JSON.parse(dataEl.textContent);
  const isAr = data.lang === 'ar';
  // Longest side of the customer's photo before upload. The server sets it (Try-On Settings,
  // "photo size"); smaller photos cost fewer input tokens.
  let maxSide = 768;
  const text = {
    remaining: (n) => (isAr ? `باقي لك ${n} من 3 محاولات النهارده` : `${n} of 3 try-ons left today`),
    invalidPhone: isAr ? 'اكتب رقم موبايل مصري صح (11 رقم يبدأ بـ 01).' : 'Enter a valid Egyptian mobile number (11 digits starting with 01).',
    consent: isAr ? 'لازم توافق على التواصل بالواتساب علشان تكمل.' : 'Please agree to WhatsApp contact to continue.',
    phoneTaken: isAr ? 'الرقم ده مسجّل على حساب تاني. استخدم رقم تاني.' : 'This number is already used on another account. Please use a different one.',
    unavailable: isAr ? 'خدمة التجربة مش متاحة دلوقتي. جرّب تاني بعدين.' : 'Try-on is not available right now. Please check back later.',
    busy: isAr
      ? 'وصلنا للحد الأقصى للتجارب النهارده. الخدمة هترجع بكرة.'
      : "We've reached today's try-on limit. The service will be back tomorrow.",
    tooFast: isAr ? 'استنى دقيقة وجرّب تاني.' : 'Please wait a minute and try again.',
    networkLimit: isAr
      ? 'وصلنا للحد الأقصى للتجارب من الشبكة دي النهارده. جرّب تاني بكرة أو من شبكة تانية.'
      : "We've reached today's try-on limit for this network. Please try again tomorrow or from another network.",
    failed: isAr
      ? 'حصلت مشكلة ومقدرناش نعمل الصورة. جرّب تاني بعد شوية أو بصورة تانية.'
      : "Something went wrong and we couldn't create the image. Please try again or use another photo.",
  };

  const productForm = document.querySelector('product-form-component form[action*="/cart/add"]');
  if (!productForm) return;

  const openButton = buttonTemplate.content.firstElementChild.cloneNode(true);
  productForm.insertAdjacentElement('afterend', openButton);

  const $ = (selector) => modal.querySelector(selector);
  const phoneForm = $('[data-step="phone"]');
  const phoneInput = $('[data-tryon-phone]');
  const consentInput = $('[data-tryon-consent]');
  const phoneError = $('[data-tryon-phone-error]');
  const fileInput = $('[data-tryon-file]');
  const preview = $('[data-tryon-preview]');
  const dropText = $('[data-tryon-drop-text]');
  const garmentImg = $('[data-tryon-garment]');
  const goButton = $('[data-tryon-go]');
  const resultImg = $('[data-tryon-result]');
  const errorEl = $('[data-tryon-error]');

  let personBlob = null;
  let ticket = null;
  let singleImage = false;

  // Random ID for this browser, so the server can also cap try-ons per device (not per account only).
  const deviceId = (() => {
    const key = 'ss-tryon-device';
    try {
      let id = localStorage.getItem(key);
      if (!id) {
        id = crypto.randomUUID();
        localStorage.setItem(key, id);
      }
      return id;
    } catch {
      return crypto.randomUUID();
    }
  })();

  const currentVariantId = () => productForm.querySelector('[name="id"]')?.value;
  const currentGarmentUrl = () => data.variants[currentVariantId()] || data.image;

  // Keep the last result per product and colour (the garment photo stands in for the colour),
  // so closing and reopening the popup shows it again instead of spending another try-on.
  // Session storage only: it stays on this device and clears when the tab closes.
  const resultCache = new Map();
  const cacheKey = () => `ss-tryon:${data.handle}|${currentGarmentUrl()}`;
  const readCachedResult = () => {
    const key = cacheKey();
    if (resultCache.has(key)) return resultCache.get(key);
    try {
      const stored = JSON.parse(sessionStorage.getItem(key) || 'null');
      if (stored) resultCache.set(key, stored);
      return stored;
    } catch {
      return null;
    }
  };
  const saveCachedResult = (result) => {
    const key = cacheKey();
    resultCache.set(key, result);
    try {
      sessionStorage.setItem(key, JSON.stringify(result));
    } catch {
      // Storage full or blocked: the in-memory copy still covers this page view.
    }
  };

  const showStep = (name) => {
    modal.querySelectorAll('[data-step]').forEach((step) => {
      step.hidden = step.dataset.step !== name;
    });
  };

  const showRemaining = (n) => {
    modal.querySelectorAll('[data-tryon-remaining]').forEach((el) => {
      el.textContent = text.remaining(n);
    });
  };

  const showError = (message) => {
    errorEl.textContent = message;
    showStep('error');
  };

  // Ask our server (through the Shopify App Proxy) where this customer stands.
  const refreshStatus = async () => {
    if (!data.loggedIn) return showStep('login');
    showStep('loading');
    try {
      const response = await fetch(`${data.proxy}/status?device=${encodeURIComponent(deviceId)}`, {
        credentials: 'same-origin',
        cache: 'no-store',
      });
      const status = await response.json();
      if (!status.loggedIn) return showStep('login');
      if (status.unavailable) return showError(text.unavailable);
      if (status.busy) return showError(text.busy);
      if (!status.hasPhone) return showStep('phone');
      if (status.remaining <= 0) return showStep('limit');
      ticket = status.ticket;
      if (Number(status.photoSize) > 0) maxSide = Number(status.photoSize);
      singleImage = status.singleImage === true;
      showRemaining(status.remaining);
      showStep('pick');
    } catch {
      showError(text.failed);
    }
  };

  const open = () => {
    garmentImg.src = currentGarmentUrl();
    modal.hidden = false;
    document.documentElement.style.overflow = 'hidden';
    const cached = readCachedResult();
    if (cached) {
      resultImg.src = cached.image;
      showRemaining(cached.remaining);
      showStep('result');
      return;
    }
    refreshStatus();
  };

  const close = () => {
    modal.hidden = true;
    document.documentElement.style.overflow = '';
  };

  // Shrink large phone photos before upload so the request stays fast.
  const resizeImage = (file) =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, maxSide / Math.max(img.width, img.height));
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
        canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('resize'))), 'image/jpeg', 0.9);
        URL.revokeObjectURL(img.src);
      };
      img.onerror = reject;
      img.src = URL.createObjectURL(file);
    });

  // Single-image mode (set on the server): product photo on the left, customer on the right,
  // both scaled to the same height, sent as one picture so it costs one image instead of two.
  const loadImage = (src, crossOrigin) =>
    new Promise((resolve, reject) => {
      const img = new Image();
      if (crossOrigin) img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = src;
    });

  const buildSingleImage = async (person) => {
    const personUrl = URL.createObjectURL(person);
    const [garmentPic, personPic] = await Promise.all([
      loadImage(currentGarmentUrl(), true),
      loadImage(personUrl, false),
    ]);
    URL.revokeObjectURL(personUrl);
    const height = Math.min(maxSide, personPic.height);
    const garmentWidth = Math.round((garmentPic.width / garmentPic.height) * height);
    const personWidth = Math.round((personPic.width / personPic.height) * height);
    const canvas = document.createElement('canvas');
    canvas.width = garmentWidth + personWidth;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(garmentPic, 0, 0, garmentWidth, height);
    ctx.drawImage(personPic, garmentWidth, 0, personWidth, height);
    return new Promise((resolve, reject) =>
      canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('combine'))), 'image/jpeg', 0.9)
    );
  };

  phoneInput.addEventListener('input', () => {
    phoneInput.value = phoneInput.value.replace(/\D/g, '').slice(0, 11);
  });

  phoneForm.addEventListener('submit', async (event) => {
    event.preventDefault();
    phoneError.hidden = true;
    const phone = phoneInput.value.trim();
    if (!/^01[0125]\d{8}$/.test(phone)) {
      phoneError.textContent = text.invalidPhone;
      phoneError.hidden = false;
      return;
    }
    if (!consentInput.checked) {
      phoneError.textContent = text.consent;
      phoneError.hidden = false;
      return;
    }
    try {
      const response = await fetch(`${data.proxy}/phone`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone, consent: true }),
      });
      const result = await response.json();
      if (!result.ok) {
        phoneError.textContent = result.error === 'phone_taken' ? text.phoneTaken : text.invalidPhone;
        phoneError.hidden = false;
        return;
      }
      refreshStatus();
    } catch {
      showError(text.failed);
    }
  });

  fileInput.addEventListener('change', async () => {
    const file = fileInput.files?.[0];
    if (!file) return;
    personBlob = await resizeImage(file);
    preview.src = URL.createObjectURL(personBlob);
    preview.hidden = false;
    dropText.hidden = true;
    goButton.disabled = false;
  });

  goButton.addEventListener('click', async () => {
    if (!personBlob || !ticket) return;
    showStep('wait');
    const form = new FormData();
    form.append('ticket', ticket);
    form.append('handle', data.handle);
    form.append('variant', currentVariantId() || '');
    form.append('device', deviceId);
    try {
      // If the product photo can't be read into the canvas, fall back to sending two images.
      const combined = singleImage ? await buildSingleImage(personBlob).catch(() => null) : null;
      if (combined) form.append('layout', 'single');
      form.append('photo', combined || personBlob, 'customer.jpg');
      const response = await fetch(`${data.worker}/tryon`, { method: 'POST', body: form });
      const result = await response.json();
      if (result.error === 'daily_limit') return showStep('limit');
      if (result.error === 'login_required') return refreshStatus();
      if (result.error === 'unavailable') return showError(text.unavailable);
      if (result.error === 'busy') return showError(text.busy);
      if (result.error === 'too_fast') return showError(text.tooFast);
      if (result.error === 'network_limit') return showError(text.networkLimit);
      if (!response.ok || !result.image) return showError(text.failed);
      resultImg.src = result.image;
      showRemaining(result.remaining);
      saveCachedResult({ image: result.image, remaining: result.remaining });
      showStep('result');
    } catch {
      showError(text.failed);
    }
  });

  openButton.addEventListener('click', open);

  // The same action as a small pill on the top corner of every gallery photo.
  const pillTemplate = document.getElementById('SuitShopTryOnPill');
  if (pillTemplate) {
    document.querySelectorAll('media-gallery .product-media-container--image').forEach((slide) => {
      // The zoom dialog repeats every photo; keep the pill on the page gallery only.
      if (slide.closest('dialog')) return;
      slide.append(pillTemplate.content.firstElementChild.cloneNode(true));
    });
    // The theme opens the photo zoom from a capture listener on document, which runs before
    // anything on the pill itself. Catch pill clicks earlier, on window, so only the try-on opens.
    window.addEventListener(
      'click',
      (event) => {
        if (!(event.target instanceof Element) || !event.target.closest('.suit-tryon__pill')) return;
        event.preventDefault();
        event.stopPropagation();
        open();
      },
      true
    );
  }
  modal.querySelectorAll('[data-tryon-close]').forEach((el) => el.addEventListener('click', close));
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && !modal.hidden) close();
  });

  modal.querySelectorAll('[data-tryon-again]').forEach((el) => el.addEventListener('click', refreshStatus));

  $('[data-tryon-add]').addEventListener('click', () => {
    close();
    productForm.querySelector('button[type="submit"], button[name="add"]')?.click();
  });
})();
